import React, { StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root from '@fs/zion-root'
import { Router, useLocation } from '@fs/zion-router'
import { appPath, sentryDSN } from '@fs/zion-config'
import { AppLayout, colors } from '@fs/zion-ui'
import { getSentryConfig } from '@fs/tree-config'
import type { Flags } from '@fs/zion-flags'
import { FeatureFlagsProvider } from '@fs/zion-flags'
import { NoticeLoading } from '@fs/zion-icon'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import DarkModeProvider, { useDarkMode } from './DarkModeProvider'
import ContrastModeProvider, { useContrastMode } from './ContrastModeProvider'
import usePreloadPreferences from './data-service/usePreloadPreferences'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'

const EmptyLoading = (): JSX.Element => <div />

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = (
  process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
) as Flags | undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    getSentryConfig({
      ignoreErrors: [/.*Error looking up record in object store by key range*/],
      sampleRate: 0.02,
      integrations: [new Integrations.BrowserTracing({ tracingOrigins: ['localhost', /^\//] })],
      tracesSampleRate: 0.001,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch ones like dark mode.
const TopLevelAppWrapper = ({ children }): JSX.Element => {
  // Pre-cache all preferences that are used in the app
  usePreloadPreferences()

  return children
}

const Page = (): JSX.Element => {
  const location = useLocation()
  const { isHighContrast } = useContrastMode()
  let backgroundColor = isHighContrast ? colors.gray05 : colors.gray03
  if (/(research-help|print-view|changelog)/.test(location.pathname)) {
    backgroundColor = colors.gray00
  } else if (location.pathname.includes('/merge/')) backgroundColor = colors.gray05

  return (
    <AppLayout fullWidth backgroundColor={backgroundColor}>
      <App />
    </AppLayout>
  )
}

const PageWrapper = (): JSX.Element => {
  const [isDarkMode] = useDarkMode()

  const hideHeaderFooter = window.location.pathname.includes('/print-view/')

  return (
    <Router basename={base} handledExternalRoutes={[{ path: '/tree/person' }]}>
      {/* This app has <Subnav> inside <main>. The skipLinkId which matches the id in App.js tells screen readers to skip the subnav and go to the main content */}
      <Root
        skipLinkId="skipToMainContent"
        theme={isDarkMode ? 'night' : 'day'}
        analytics
        header={{ hidden: hideHeaderFooter }}
        footer={{ hidden: hideHeaderFooter }}
      >
        <Page />
      </Root>
    </Router>
  )
}

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)

  return (
    <TopLevelAppWrapper>
      <DarkModeProvider>
        <ContrastModeProvider>
          <PageWrapper />
        </ContrastModeProvider>
      </DarkModeProvider>
    </TopLevelAppWrapper>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => (
  <StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FeatureFlagsProvider mockFlags={mockFlags} LoadingFallback={EmptyLoading}>
        <FrontierWrapper />
      </FeatureFlagsProvider>
    </Suspense>

    {/* hide in production build */}
    {process.env.NODE_ENV === 'development' && <RebuggerClient />}
  </StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
